import { twJoin } from 'tailwind-merge';

import Button from 'molecules/Button';
import MarketoForm from 'molecules/Form';
import Section from 'molecules/Section';
import { splitSectionProps } from 'molecules/Section/config';

import {
  descriptionStyles,
  headingStyles,
  resourceConvContainer,
} from 'organisms/Cards/ResourceConversionPanel/styles';

import { useBuilderReference } from 'utils/hooks/useBuilderReference';

import type { ResourceConversionPanelConfigProps } from 'organisms/Cards/ResourceConversionPanel/config';
import type { FC } from 'react';

interface ResourceConversionPanelProps extends Omit<ResourceConversionPanelConfigProps, 'button'> {
  /**
   * An optional CSS class name to apply to the component.
   */
  className?: string;
  button?: {
    label?: string;
    href?: string;
    onClick?: () => void;
  };
  theme?: 'dark' | 'green';
}

const ResourceConversionPanel: FC<ResourceConversionPanelProps> = props => {
  const { sectionProps, rest } = splitSectionProps(props);
  const { variation, heading, description, form, button, className, theme, standalone } = rest;

  const hasPreloadedForm = !!form?.value?.data;

  const formRefData = useBuilderReference(
    !hasPreloadedForm ? form?.model : undefined,
    !hasPreloadedForm ? form?.id : undefined,
  );
  const formData = hasPreloadedForm ? form.value.data : formRefData;

  const panel = (
    <div className={twJoin('@container', className)}>
      <div className={resourceConvContainer(theme ? { theme } : { variation })}>
        <div />
        <div className="z-10 flex w-full flex-col gap-6 @lg:gap-8 @5xl:flex-row @5xl:justify-between">
          <div className="flex flex-col gap-4">
            <span className={headingStyles(theme ? { theme } : { variation })}>{heading}</span>
            <span className={descriptionStyles(theme ? { theme } : { variation })}>{description}</span>
          </div>
          {variation === 'form' && formData && (
            <div className="w-full max-w-[470px]">
              <MarketoForm formData={formData as FormReference} />
            </div>
          )}

          {button && (
            <div className="w-full @lg:w-fit">
              <Button
                hierarchy="tertiary"
                className={twJoin(
                  theme === 'dark'
                    ? 'bg-primary-button-default !text-gray-900'
                    : '!bg-buttonLightGradient !text-gray-900',
                  '!w-full',
                )}
                label={button.label}
                href={button.href}
                onClick={button.onClick}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return standalone ? <Section {...sectionProps}>{panel}</Section> : panel;
};

export default ResourceConversionPanel;
